import { Reveal } from "../components/Reveal";
import { Compass, Mountain, Waves, Sparkles, Youtube } from "lucide-react";
import { Link } from "react-router-dom";
import { VIJAY_DATLA_PORTRAIT, YOUTUBE_CHANNEL, THEME_SONG_YT_ID } from "../lib/episodes";
import { LOGOS } from "../lib/logos";

const PILLARS = [
  { icon: Compass,  title: "Spiritual Journeys",       text: "Sacred places. Ancient traditions. Divine stories." },
  { icon: Mountain, title: "Historical Explorations",   text: "Walking through rich heritage and forgotten kingdoms." },
  { icon: Waves,    title: "Adventure Experiences",     text: "Thrills that challenge the body and awaken the soul." },
  { icon: Sparkles, title: "Heartwarming Conversations", text: "Strip the noise. Meet the real person beneath." },
];

export default function About() {
  return (
    <main data-testid="about-page" className="relative">
      {/* HERO */}
      <section className="relative w-screen h-[90vh] overflow-hidden flex items-end" style={{ background: "#cfe3ef" }}>
        <img
          src="https://customer-assets.emergentagent.com/job_virtual-journey-hub-1/artifacts/6wzlmpzr_Screenshot%202026-05-20%20172129.png"
          alt="Soul Trip official poster"
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: "cover", objectPosition: "center 40%", zIndex: 1, animation: "aboutZoom 22s ease-in-out infinite alternate" }}
        />
        <div
          className="absolute inset-0"
          style={{
            zIndex: 2,
            background:
              "linear-gradient(to bottom, transparent 0%, transparent 60%, rgba(5,4,3,.55) 82%, var(--coal) 100%)",
          }}
        />

        <div className="relative z-[5] px-6 md:px-16 pb-16 max-w-3xl">
          <span className="st-eyebrow" style={{ textShadow: "0 1px 8px rgba(0,0,0,.55)" }}>About · The Show</span>
          <div className="inline-block mt-4 px-4 py-2 st-cinzel text-[0.5rem] tracking-[0.5em] uppercase text-[var(--gold-lt)]"
               style={{ borderColor: "rgba(201,160,74,.4)", border: "1px solid rgba(201,160,74,.4)", background: "rgba(5,4,3,.5)", backdropFilter: "blur(8px)" }}>
            FIRST TIME IN INDIA · ONE OF ITS KIND SHOW
          </div>
          <p className="mt-6 st-infant text-base md:text-lg text-[var(--cream)] max-w-lg leading-relaxed"
             style={{ textShadow: "0 1px 8px rgba(0,0,0,.6)" }}>
            A travel-based adventure talk show — conceived, hosted and directed by Vijay Datla,
            produced by <span className="text-[var(--gold-lt)]">Gandabherunda Arts</span>.
            Season 1 powered by <span className="text-[var(--gold-lt)]">Tamada Media</span> &amp;
            presented by <span className="text-[var(--gold-lt)]">Rizwan Entertainments</span>.
            Music by <span className="text-[var(--gold-lt)]">K-Music</span>. Streaming on{" "}
            <span className="text-[var(--gold-lt)]">ETV Win</span>{" "}
            with <span className="text-[var(--gold-lt)]">TV9</span> as broadcasting partner (soon).
          </p>
        </div>

        <style>{`@keyframes aboutZoom { from { transform: scale(1.0); } to { transform: scale(1.08); } }`}</style>
      </section>

      {/* THE THEME */}
      <section className="relative w-screen py-24 md:py-32 px-5 md:px-16 overflow-hidden" style={{ background: "var(--dusk)" }}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(201,160,74,.05) 0%, transparent 70%), repeating-linear-gradient(0deg, transparent, transparent 59px, rgba(201,160,74,.018) 60px), repeating-linear-gradient(90deg, transparent, transparent 59px, rgba(201,160,74,.018) 60px)",
          }}
        />
        <Reveal className="relative z-[2] max-w-3xl mx-auto text-center">
          <span className="block st-cormorant text-[6rem] md:text-[10rem] leading-[0.4] text-[rgba(201,160,74,0.12)]">"</span>
          <h2 className="st-cormorant text-2xl md:text-4xl italic font-light text-[var(--cream)] leading-[1.3] -mt-2">
            When distractions are removed,
            <strong className="block not-italic font-light text-[var(--gold-lt)] mt-2">we meet the real person.</strong>
          </h2>
          <div className="st-rule mx-auto mt-8"/>
          <div className="mt-12 flex flex-col md:flex-row gap-12 md:gap-16 justify-center pt-10 border-t" style={{ borderColor: "rgba(201,160,74,.1)" }}>
            <div className="max-w-xs mx-auto">
              <p className="st-infant text-base text-[var(--fog)] leading-relaxed">"Once a year go someplace you've never been before."</p>
              <div className="mt-3 st-cinzel text-[0.46rem] tracking-[0.4em] uppercase text-[var(--gold-dk)]">— Dalai Lama</div>
            </div>
            <div className="max-w-xs mx-auto">
              <p className="st-infant text-base text-[var(--fog)] leading-relaxed">"Feel the real travel. Real locations. Real conversation. Real vibe."</p>
              <div className="mt-3 st-cinzel text-[0.46rem] tracking-[0.4em] uppercase text-[var(--gold-dk)]">— Soul Trip · Mantra</div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* CONCEPT PITCH */}
      <section className="relative w-screen py-24 md:py-32 px-5 md:px-16 overflow-hidden" style={{ background: "var(--coal)" }}>
        <div
          className="absolute inset-0"
          style={{
            background:
              "url('https://upload.wikimedia.org/wikipedia/commons/thumb/9/97/The_Earth_seen_from_Apollo_17.jpg/1024px-The_Earth_seen_from_Apollo_17.jpg') center/cover",
            filter: "brightness(.09) saturate(.3)",
            opacity: 0.9,
          }}
        />
        <div className="relative z-[2] max-w-6xl mx-auto">
          <Reveal className="text-center mb-12">
            <span className="st-eyebrow">The Concept</span>
            <h2 className="mt-3 st-cormorant text-3xl md:text-5xl italic font-light text-[var(--cream)]">
              Imagine waking up to <em className="not-italic text-[var(--gold-lt)]">nature itself</em>.
            </h2>
            <div className="st-rule mx-auto mt-5"/>
          </Reveal>

          <div className="grid md:grid-cols-[1.2fr_1fr] gap-12 md:gap-20 items-start">
            <Reveal>
              <div className="st-infant text-base md:text-[1.12rem] text-[var(--fog)] leading-[1.95] space-y-5">
                <p>
                  Imagine waking up to mountains drinking the dawn. To a tribal grandmother humming a song older than your language.
                  To a fisherman pointing at a horizon that was once a country.
                </p>
                <p>
                  Soul Trip puts <strong>Vijay Datla</strong> in conversation with India's most evocative voices — actors, artists,
                  ascetics — at <strong>the very places that shaped them</strong> or the ones they've never dared to visit.
                </p>
                <p>
                  No green screen. No teleprompter. No filter. <strong>Just the soul of the place</strong>, and the soul of the person, meeting on camera.
                </p>
              </div>
            </Reveal>

            <Reveal delay={140}>
              <div className="flex flex-col">
                {PILLARS.map((p, i) => (
                  <div key={p.title} className="flex items-start gap-4 py-5 border-b" style={{ borderColor: i === 0 ? "rgba(201,160,74,.08)" : "rgba(201,160,74,.08)", borderTop: i === 0 ? "1px solid rgba(201,160,74,.08)" : "none" }}>
                    <p.icon size={20} className="text-[var(--gold)] mt-1 shrink-0 opacity-70"/>
                    <div>
                      <div className="st-cinzel text-[0.66rem] tracking-[0.2em] uppercase text-[var(--gold)] font-semibold">{p.title}</div>
                      <div className="mt-1 st-infant text-[0.93rem] text-[var(--fog)] leading-snug">{p.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HOST DEEP DIVE */}
      <section className="relative w-screen py-24 md:py-32 px-5 md:px-16" style={{ background: "var(--dusk)" }}>
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-12">
            <span className="st-eyebrow">Meet The Host</span>
            <h2 className="mt-3 st-cormorant text-3xl md:text-5xl italic font-light text-[var(--cream)]">
              A storyteller raised by stages — <em className="not-italic text-[var(--gold-lt)]">and then by the road.</em>
            </h2>
            <div className="st-rule mx-auto mt-5"/>
          </Reveal>

          <Reveal>
            <div
              className="relative w-full mx-auto"
              style={{
                background: "var(--night)",
                border: "1px solid rgba(201,160,74,.22)",
                overflow: "hidden",
                maxWidth: 1100,
              }}
            >
              <img
                src={VIJAY_DATLA_PORTRAIT}
                alt="Vijay Datla"
                className="block w-full h-auto"
              />
            </div>
          </Reveal>

          <div className="mt-12 grid md:grid-cols-2 gap-10 md:gap-16 max-w-5xl mx-auto items-start">
            <Reveal delay={120}>
              <div className="st-infant text-[1.05rem] text-[var(--fog)] leading-[1.85] space-y-4">
                <p>
                  Theatre actor turned assistant director turned lead in Telugu feature films, Vijay Datla has spent
                  a decade alternating between camera flash and forest silence.
                </p>
                <p>
                  Soul Trip began as a personal vow — to record the kind of conversations that don't happen in studios.
                  The kind that need a campfire, a temple bell, or a wave breaking nearby.
                </p>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div className="grid grid-cols-3 gap-px" style={{ background: "rgba(201,160,74,.15)" }}>
                {[
                  { n: "12+", l: "Years In Cinema" },
                  { n: "06", l: "Episodes · S1" },
                  { n: "03", l: "Mountain Ranges" },
                ].map((s) => (
                  <div key={s.l} className="p-5 text-center" style={{ background: "var(--coal)" }}>
                    <div className="st-cormorant text-3xl md:text-4xl text-[var(--gold-lt)]">{s.n}</div>
                    <div className="st-cinzel text-[0.45rem] tracking-[0.3em] uppercase text-[var(--fog)] mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THEME SONG + YOUTUBE CHANNEL */}
      <section className="relative w-screen py-24 md:py-32 px-5 md:px-16 overflow-hidden" style={{ background: "var(--coal)" }}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 50% 50% at 70% 30%, rgba(232,40,40,.06) 0%, transparent 60%), radial-gradient(ellipse 50% 50% at 20% 80%, rgba(201,160,74,.05) 0%, transparent 60%)",
          }}
        />
        <div className="relative z-[2] max-w-6xl mx-auto">
          <Reveal className="text-center mb-14">
            <span className="st-eyebrow">The Anthem</span>
            <h2 className="mt-3 st-cormorant text-3xl md:text-5xl italic font-light text-[var(--cream)]">
              The Soul Trip <em className="not-italic text-[var(--gold-lt)]">Theme</em>.
            </h2>
            <div className="st-rule mx-auto mt-5"/>
            <p className="mt-5 st-infant text-base md:text-lg text-[var(--fog)] max-w-xl mx-auto">
              Music by K-Music · Composed by Sahithya Sagar. Press play — let it find you.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-[1.4fr_1fr] gap-8 md:gap-12 items-center">
            <Reveal>
              <div
                className="relative w-full"
                style={{
                  aspectRatio: "16 / 9",
                  background: "var(--night)",
                  border: "1px solid rgba(201,160,74,.25)",
                  overflow: "hidden",
                }}
              >
                <iframe
                  data-testid="theme-song-iframe"
                  title="Soul Trip — Official Theme"
                  src={`https://www.youtube.com/embed/${THEME_SONG_YT_ID}?modestbranding=1&rel=0`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                  style={{ border: 0 }}
                />
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="p-6 md:p-8" style={{ background: "rgba(8,6,4,.7)", border: "1px solid rgba(232,40,40,.3)" }}>
                <div className="flex items-center gap-2">
                  <Youtube size={16} className="text-[#ff7a7a]"/>
                  <span className="st-cinzel text-[0.5rem] tracking-[0.4em] uppercase text-[#ff9595]">@soultrip-talks</span>
                </div>
                <h3 className="mt-4 st-cormorant text-2xl md:text-3xl italic font-light text-[var(--cream)]">
                  The full universe lives on YouTube.
                </h3>
                <p className="mt-3 st-infant text-base text-[var(--fog)] leading-relaxed">
                  Trailers, full episodes, behind-the-scenes vlogs, and Vijay's field notes — every Soul Trip frame is on the channel.
                </p>
                <a
                  data-testid="about-cta-youtube"
                  href={YOUTUBE_CHANNEL}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-2 st-btn st-interactive"
                  style={{ borderColor: "rgba(255,80,80,.55)", color: "#ff9595" }}
                >
                  <Youtube size={12}/> Subscribe on YouTube
                </a>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-px" style={{ background: "rgba(201,160,74,.15)" }}>
                {[
                  { n: "06", l: "Episodes · S1" },
                  { n: "07", l: "Trailers Live" },
                  { n: "S2", l: "Brewing Soon" },
                ].map((s) => (
                  <div key={s.l} className="p-4 text-center" style={{ background: "var(--coal)" }}>
                    <div className="st-cormorant text-2xl md:text-3xl text-[var(--gold-lt)]">{s.n}</div>
                    <div className="st-cinzel text-[0.42rem] tracking-[0.3em] uppercase text-[var(--fog)] mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal className="mt-16 text-center" delay={150}>
            <p className="st-infant text-base text-[var(--fog)] max-w-xl mx-auto">
              Behind every episode: cameras that survived monsoons, microphones that caught whispers,
              and a crew that learned to listen.
            </p>
            <Link to="/contact" className="mt-8 inline-flex st-btn st-interactive" data-testid="about-cta-contact">
              Work with us
            </Link>
          </Reveal>

          {/* Season 1 End Card — cinematic wrap poster */}
          <Reveal className="mt-20 md:mt-28 text-center" delay={200}>
            <span className="st-eyebrow">Season 1 · Final Frame</span>
            <h2 className="mt-3 st-cormorant text-3xl md:text-5xl italic font-light text-[var(--cream)]">
              The Last Word
            </h2>
            <div className="st-rule mx-auto mt-5"/>
            <p className="mt-5 st-cormorant italic text-base md:text-lg text-[var(--fog)] max-w-2xl mx-auto leading-relaxed">
              A bow to every name that made Season 1 possible — on screen, behind the lens, in the edit bay.
            </p>

            <figure
              className="relative mx-auto overflow-hidden mt-10 md:mt-12"
              style={{
                maxWidth: "1000px",
                border: "1px solid rgba(201,160,74,.28)",
                boxShadow: "0 30px 80px rgba(0,0,0,.55), 0 0 0 1px rgba(201,160,74,.08) inset",
                background: "var(--ink)",
              }}
            >
              <img
                src={LOGOS.s1EndCard}
                alt="Soul Trip — Season 1 end card with full crew credits"
                data-testid="about-s1-end-card-img"
                className="block w-full h-auto"
                style={{ objectFit: "contain" }}
                loading="lazy"
              />
              <span className="absolute top-0 left-0 w-6 h-6 border-t border-l border-[var(--gold)] opacity-60" />
              <span className="absolute top-0 right-0 w-6 h-6 border-t border-r border-[var(--gold)] opacity-60" />
              <span className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-[var(--gold)] opacity-60" />
              <span className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-[var(--gold)] opacity-60" />
            </figure>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
